import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { useAuth } from '@/hooks/useAuth';
import { useCollection } from '@/firebase/firestore/use-collection';
import { sanitizeFirestoreData, cn } from '@/lib/utils';
import type { Jarticard, JarticardSobre, JarticardType, UserJarticard } from '@/lib/types';
import { JARTICARD_TYPES, JARTICARD_TYPE_LABELS } from '@/lib/types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import {
  adminCreateJarticard, adminUpdateJarticard, adminDeleteJarticard,
  adminSaveSobre, adminDeleteSobre, uploadJarticardImage,
  searchUsersByNickname, adminGetUserJarticards, adminGetTopCollections,
} from '@/lib/actions';
import { JarticardFrame } from '@/components/jarticards/JarticardFrame';
import { Loader2, Plus, Pencil, Trash2, Search, Layers, ImagePlus, Coins, Trophy, Medal } from 'lucide-react';

export default function AdminJarticardsPage() {
  const { user, userData, loading, jartinetData } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && userData?.role !== 'admin') navigate('/jartinet', { replace: true });
  }, [userData, loading, navigate]);

  const cardsQuery = useMemo(() => query(collection(db, 'jarticards'), orderBy('number', 'asc')), []);
  const { data: rawCards } = useCollection<Jarticard>(cardsQuery);
  const sobresQuery = useMemo(() => collection(db, 'jarticard_sobres'), []);
  const { data: rawSobres } = useCollection<JarticardSobre>(sobresQuery);

  const cards = useMemo(() => (rawCards || []).map((c) => sanitizeFirestoreData(c) as Jarticard), [rawCards]);
  const sobres = useMemo(() => (rawSobres || []).map((s) => sanitizeFirestoreData(s) as JarticardSobre), [rawSobres]);

  if (loading || userData?.role !== 'admin') {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  // Escudos disponibles (hermandades + bandas) para asignar a una carta
  const crestOptions = [
    ...(jartinetData?.hermandades || []).map((h: any) => ({ value: h.escudoUrl, label: `Hermandad: ${h.nombreCompleto || h.nombre}` })),
    ...(jartinetData?.bandas || []).map((b: any) => ({ value: b.escudoUrl, label: `Banda: ${b.nombreCompleto}` })),
  ].filter((o) => !!o.value);

  return (
    <div className="space-y-6 pb-12">
      <div className="flex items-center gap-3">
        <div className="grid h-12 w-12 place-items-center rounded-2xl border border-amber-400/30 bg-amber-500/15 text-amber-300">
          <Layers className="h-6 w-6" />
        </div>
        <div>
          <h1 className="font-headline text-3xl font-bold">Gestión de JartiCards</h1>
          <p className="text-sm text-muted-foreground">Cartas, sobres de la tienda y colecciones de usuarios.</p>
        </div>
      </div>

      <Tabs defaultValue="cartas">
        <TabsList>
          <TabsTrigger value="cartas">Cartas ({cards.length})</TabsTrigger>
          <TabsTrigger value="sobres">Sobres ({sobres.length})</TabsTrigger>
          <TabsTrigger value="colecciones">Colecciones</TabsTrigger>
        </TabsList>

        <TabsContent value="cartas" className="mt-6">
          <CardsManager cards={cards} crestOptions={crestOptions} />
        </TabsContent>
        <TabsContent value="sobres" className="mt-6">
          <SobresManager sobres={sobres} />
        </TabsContent>
        <TabsContent value="colecciones" className="mt-6">
          <CollectionsViewer cards={cards} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Gestión de cartas ──

function CardsManager({ cards, crestOptions }: { cards: Jarticard[]; crestOptions: { value: string; label: string }[] }) {
  const [editing, setEditing] = useState<Jarticard | 'new' | null>(null);

  return (
    <div className="space-y-5">
      <Button onClick={() => setEditing('new')}><Plus className="h-4 w-4" /> Nueva carta</Button>

      {cards.length === 0 ? (
        <p className="text-sm text-muted-foreground">No hay cartas todavía.</p>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
          {cards.map((card) => (
            <div key={card.id} className="space-y-1.5">
              <JarticardFrame thumb card={card} owned onClick={() => setEditing(card)} />
              <p className="truncate text-center text-xs text-muted-foreground">#{card.number} {card.name}</p>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <CardEditorDialog
          card={editing === 'new' ? null : editing}
          crestOptions={crestOptions}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function CardEditorDialog({ card, crestOptions, onClose }: { card: Jarticard | null; crestOptions: { value: string; label: string }[]; onClose: () => void }) {
  const { toast } = useToast();
  const [type, setType] = useState<JarticardType>(card?.type || 'titular');
  const [number, setNumber] = useState<string>(card ? String(card.number) : '');
  const [name, setName] = useState(card?.name || '');
  const [imageUrl, setImageUrl] = useState(card?.imageUrl || '');
  const [crestUrl, setCrestUrl] = useState<string>(card?.crestUrl || 'none');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleUpload = async (file: File) => {
    setUploading(true);
    const res = await uploadJarticardImage(file);
    setUploading(false);
    if (res.success && res.url) setImageUrl(res.url);
    else toast({ title: 'Error', description: res.message, variant: 'destructive' });
  };

  const handleSave = async () => {
    if (!name.trim() || !imageUrl) {
      toast({ title: 'Faltan datos', description: 'Elige tipo, sube una foto y pon un nombre.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    const payload: any = { type, name: name.trim(), imageUrl, crestUrl: crestUrl === 'none' ? null : crestUrl };
    if (card) {
      // Editar: solo mandamos number si cambia (dispara la reordenación en el backend).
      if (number.trim() !== '' && Number(number) !== card.number) payload.number = Math.floor(Number(number));
    } else if (number.trim() !== '') {
      // Crear: número opcional = posición donde insertar (el resto se recoloca solo).
      payload.number = Math.floor(Number(number));
    }
    const res = card ? await adminUpdateJarticard(card.id, payload) : await adminCreateJarticard(payload);
    setSaving(false);
    toast({ title: res.success ? 'Guardado' : 'Error', description: res.message, variant: res.success ? undefined : 'destructive' });
    if (res.success) onClose();
  };

  const preview: Jarticard = { id: 'preview', number: card ? (number.trim() === '' ? 0 : Math.floor(Number(number))) : (card?.number ?? 0), type, name: name || 'Nombre de la carta', imageUrl, crestUrl: crestUrl === 'none' ? null : crestUrl };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{card ? `Editar carta #${card.number}` : 'Nueva carta'}</DialogTitle>
          <DialogDescription>Elige el tipo, la foto, el nombre y (opcional) un escudo.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 sm:grid-cols-2">
          {/* Formulario */}
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Tipo de carta</Label>
              <Select value={type} onValueChange={(v) => setType(v as JarticardType)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {JARTICARD_TYPES.map((t) => <SelectItem key={t} value={t}>{JARTICARD_TYPE_LABELS[t]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Foto de la carta</Label>
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-white/15 px-3 py-2 text-sm text-muted-foreground hover:bg-white/5">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                {imageUrl ? 'Cambiar foto' : 'Subir foto'}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])} />
              </label>
            </div>

            <div className="space-y-1.5">
              <Label>Nombre</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej. La Esperanza de Triana" />
            </div>

            <div className="space-y-1.5">
              <Label>{card ? 'Número en la colección' : 'Posición (opcional)'}</Label>
              <Input type="number" min={1} value={number} onChange={(e) => setNumber(e.target.value)} placeholder={card ? 'Nº de carta' : 'Al final si lo dejas vacío'} />
              <p className="text-xs text-muted-foreground">
                {card
                  ? 'Cambia la posición de esta carta en el álbum. El resto de cartas se recolocan automáticamente.'
                  : 'Dónde insertar la carta. Las cartas de esa posición en adelante se recolocan automáticamente. Vacío = al final.'}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label>Escudo (opcional)</Label>
              <Select value={crestUrl} onValueChange={setCrestUrl}>
                <SelectTrigger><SelectValue placeholder="Sin escudo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin escudo</SelectItem>
                  {crestOptions.map((o, i) => <SelectItem key={`${o.value}-${i}`} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Vista previa */}
          <div className="flex items-center justify-center">
            <div className="w-48">
              <JarticardFrame card={preview} owned />
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {card && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" className="text-rose-400"><Trash2 className="h-4 w-4" /> Eliminar</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Eliminar esta carta?</AlertDialogTitle>
                  <AlertDialogDescription>Se quitará de la colección. Las copias que tengan los usuarios dejarán de mostrarse.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={async () => {
                      const res = await adminDeleteJarticard(card.id);
                      toast({ title: res.success ? 'Eliminada' : 'Error', description: res.message, variant: res.success ? undefined : 'destructive' });
                      if (res.success) onClose();
                    }}
                  >Eliminar</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving || uploading}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Guardar
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Gestión de sobres ──

function SobresManager({ sobres }: { sobres: JarticardSobre[] }) {
  const [editing, setEditing] = useState<JarticardSobre | 'new' | null>(null);

  return (
    <div className="space-y-5">
      <Button onClick={() => setEditing('new')}><Plus className="h-4 w-4" /> Nuevo sobre</Button>

      {sobres.length === 0 ? (
        <p className="text-sm text-muted-foreground">No hay sobres todavía.</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {sobres.map((s) => (
            <div key={s.id} className="rounded-2xl border border-white/8 bg-card/40 p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-bold">{s.name} {s.active === false && <span className="text-xs text-rose-400">(inactivo)</span>}</h3>
                  <p className="text-xs text-muted-foreground">{s.numCards} cartas · <span className="text-amber-400">{s.price} oro</span> · {s.stockPerUser ? `${s.stockPerUser}/usuario` : 'stock ∞'}</p>
                </div>
                <Button size="icon" variant="ghost" onClick={() => setEditing(s)}><Pencil className="h-4 w-4" /></Button>
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {JARTICARD_TYPES.map((t) => (s.probabilities?.[t] ? (
                  <span key={t} className="rounded-full bg-white/5 px-2 py-0.5 text-[0.7rem]">{JARTICARD_TYPE_LABELS[t]}: {s.probabilities[t]}</span>
                ) : null))}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <SobreEditorDialog sobre={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function SobreEditorDialog({ sobre, onClose }: { sobre: JarticardSobre | null; onClose: () => void }) {
  const { toast } = useToast();
  const [name, setName] = useState(sobre?.name || '');
  const [description, setDescription] = useState(sobre?.description || '');
  const [price, setPrice] = useState<number>(sobre?.price ?? 100);
  const [numCards, setNumCards] = useState<number>(sobre?.numCards ?? 3);
  const [color, setColor] = useState(sobre?.color || '#f59e0b');
  const [active, setActive] = useState(sobre?.active !== false);
  const [stockPerUser, setStockPerUser] = useState<number>(sobre?.stockPerUser ?? 0);
  const [freeDaily, setFreeDaily] = useState<boolean>(sobre?.freeDaily === true);
  const [imageUrl, setImageUrl] = useState(sobre?.imageUrl || '');
  const [uploading, setUploading] = useState(false);
  const [probs, setProbs] = useState<Record<JarticardType, number>>(() => {
    const base = {} as Record<JarticardType, number>;
    JARTICARD_TYPES.forEach((t) => { base[t] = sobre?.probabilities?.[t] ?? (t === 'titular' ? 40 : t === 'prime' ? 2 : t === 'superprime' ? 1 : 15); });
    return base;
  });
  const [saving, setSaving] = useState(false);

  const sum = JARTICARD_TYPES.reduce((s, t) => s + (Number(probs[t]) || 0), 0);

  const handleUpload = async (file: File) => {
    setUploading(true);
    const res = await uploadJarticardImage(file);
    setUploading(false);
    if (res.success && res.url) setImageUrl(res.url);
    else toast({ title: 'Error', description: res.message, variant: 'destructive' });
  };

  const handleSave = async () => {
    if (!name.trim()) { toast({ title: 'Falta el nombre', variant: 'destructive' }); return; }
    if (sum <= 0) { toast({ title: 'Las probabilidades deben sumar más de 0', variant: 'destructive' }); return; }
    setSaving(true);
    const res = await adminSaveSobre(sobre?.id ?? null, {
      name: name.trim(), description, price: Number(price), numCards: Number(numCards),
      probabilities: probs, color, active, stockPerUser: Number(stockPerUser), imageUrl: imageUrl || null,
      freeDaily,
    } as any);
    setSaving(false);
    toast({ title: res.success ? 'Guardado' : 'Error', description: res.message, variant: res.success ? undefined : 'destructive' });
    if (res.success) onClose();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{sobre ? 'Editar sobre' : 'Nuevo sobre'}</DialogTitle>
          <DialogDescription>Configura precio, contenido y probabilidades por tipo.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Nombre</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Descripción</Label><Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} /></div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Precio (oro)</Label><Input type="number" min={0} value={price} onChange={(e) => setPrice(Number(e.target.value))} /></div>
            <div className="space-y-1.5"><Label>Nº de cartas</Label><Input type="number" min={1} value={numCards} onChange={(e) => setNumCards(Number(e.target.value))} /></div>
          </div>

          <div className="space-y-1.5">
            <Label>Stock por usuario</Label>
            <Input type="number" min={0} value={stockPerUser} onChange={(e) => setStockPerUser(Number(e.target.value))} />
            <p className="text-xs text-muted-foreground">Máximo de sobres que puede comprar cada usuario. <b>0 = ilimitado</b>.</p>
          </div>

          <div className="space-y-2 rounded-xl border border-white/8 bg-muted/30 p-3">
            <div className="flex items-center justify-between">
              <Label>Probabilidad por tipo (pesos)</Label>
              <span className={sum > 0 ? 'text-xs text-muted-foreground' : 'text-xs text-rose-400'}>Suma: {sum}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {JARTICARD_TYPES.map((t) => (
                <div key={t} className="flex items-center gap-2">
                  <span className="w-24 text-sm">{JARTICARD_TYPE_LABELS[t]}</span>
                  <Input
                    type="number" min={0} value={probs[t]}
                    onChange={(e) => setProbs((p) => ({ ...p, [t]: Number(e.target.value) }))}
                  />
                  <span className="w-10 text-right text-xs text-muted-foreground">{sum > 0 ? Math.round((probs[t] / sum) * 100) : 0}%</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Color</Label><Input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-10 p-1" /></div>
            <div className="space-y-1.5">
              <Label>Imagen (opcional)</Label>
              <label className="flex h-10 cursor-pointer items-center gap-2 rounded-lg border border-dashed border-white/15 px-3 text-sm text-muted-foreground hover:bg-white/5">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                {imageUrl ? 'Cambiar' : 'Subir'}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])} />
              </label>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-xl border border-white/8 p-3">
            <Label>Activo (visible en la tienda)</Label>
            <Switch checked={active} onCheckedChange={setActive} />
          </div>

          <div className="flex items-center justify-between rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3">
            <div>
              <Label className="text-emerald-300">Sobre gratis diario</Label>
              <p className="text-xs text-muted-foreground">Reclamable gratis una vez cada 24 h (ignora el precio).</p>
            </div>
            <Switch checked={freeDaily} onCheckedChange={setFreeDaily} />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {sobre && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" className="text-rose-400"><Trash2 className="h-4 w-4" /> Eliminar</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Eliminar este sobre?</AlertDialogTitle>
                  <AlertDialogDescription>Dejará de estar disponible en la tienda.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={async () => {
                    const res = await adminDeleteSobre(sobre.id);
                    toast({ title: res.success ? 'Eliminado' : 'Error', description: res.message, variant: res.success ? undefined : 'destructive' });
                    if (res.success) onClose();
                  }}>Eliminar</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving || uploading}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Coins className="h-4 w-4" />} Guardar
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Visor de colecciones de usuarios ──

type RankEntry = { id: string; nickname: string; unique: number; copies: number; percent: number };

function CollectionsViewer({ cards }: { cards: Jarticard[] }) {
  const { toast } = useToast();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<{ id: string; nickname: string }[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{ id: string; nickname: string } | null>(null);
  const [owned, setOwned] = useState<Map<string, number>>(new Map());
  const [loadingColl, setLoadingColl] = useState(false);

  // Ranking por defecto: colecciones más completas arriba.
  const [ranking, setRanking] = useState<RankEntry[]>([]);
  const [loadingRanking, setLoadingRanking] = useState(true);

  useEffect(() => {
    let active = true;
    setLoadingRanking(true);
    adminGetTopCollections(50).then((res) => {
      if (!active) return;
      if (res.success) setRanking(res.ranking);
      setLoadingRanking(false);
    });
    return () => { active = false; };
  }, []);

  const handleSearch = async () => {
    if (q.trim().length < 2) return;
    setSearching(true);
    const res = await searchUsersByNickname(q.trim());
    setSearching(false);
    setResults(res.users.map((u) => ({ id: u.id, nickname: u.nickname })));
  };

  const loadCollection = async (u: { id: string; nickname: string }) => {
    setSelectedUser(u);
    setLoadingColl(true);
    const res = await adminGetUserJarticards(u.id);
    setLoadingColl(false);
    const m = new Map<string, number>();
    if (res.success) (res.owned as UserJarticard[]).forEach((o) => m.set(o.cardId, o.count || 0));
    else toast({ title: 'Error', description: res.message, variant: 'destructive' });
    setOwned(m);
  };

  const uniqueOwned = cards.filter((c) => (owned.get(c.id) || 0) > 0).length;

  return (
    <div className="space-y-5">
      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar usuario por nombre…" onKeyDown={(e) => e.key === 'Enter' && handleSearch()} />
        <Button onClick={handleSearch} disabled={searching}>{searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}</Button>
      </div>

      {results.length > 0 && !selectedUser && (
        <div className="flex flex-wrap gap-2">
          {results.map((u) => (
            <Button key={u.id} variant="outline" size="sm" onClick={() => loadCollection(u)}>{u.nickname}</Button>
          ))}
        </div>
      )}

      {/* Ranking por defecto: colecciones más completas arriba */}
      {!selectedUser && results.length === 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-amber-400" />
            <h3 className="font-bold">Colecciones más completas</h3>
          </div>
          {loadingRanking ? (
            <div className="flex justify-center py-10"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
          ) : ranking.length === 0 ? (
            <p className="text-sm text-muted-foreground">Todavía no hay colecciones registradas.</p>
          ) : (
            <div className="space-y-2">
              {ranking.map((r, i) => {
                const medalColor = i === 0 ? 'text-amber-400' : i === 1 ? 'text-slate-300' : i === 2 ? 'text-amber-700' : 'text-muted-foreground';
                return (
                  <button
                    key={r.id}
                    onClick={() => loadCollection({ id: r.id, nickname: r.nickname })}
                    className="flex w-full items-center gap-3 rounded-2xl border border-white/8 bg-card/40 p-3 text-left transition-colors hover:border-primary/40 hover:bg-primary/6"
                  >
                    <div className="flex w-8 shrink-0 items-center justify-center">
                      {i < 3 ? <Medal className={cn('h-5 w-5', medalColor)} /> : <span className="text-sm font-bold text-muted-foreground">{i + 1}</span>}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold">{r.nickname}</p>
                      <p className="text-xs text-muted-foreground">{r.unique} cartas distintas · {r.copies} en total</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="font-black text-primary">{r.percent}%</p>
                      <p className="text-[0.7rem] text-muted-foreground">de la colección</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {selectedUser && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold">Colección de {selectedUser.nickname} — {uniqueOwned}/{cards.length}</h3>
            <Button variant="ghost" size="sm" onClick={() => { setSelectedUser(null); setOwned(new Map()); }}>Volver</Button>
          </div>
          {loadingColl ? (
            <div className="flex justify-center py-10"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
          ) : (
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
              {cards.map((card) => {
                const count = owned.get(card.id) || 0;
                return <JarticardFrame key={card.id} thumb card={card} owned={count > 0} count={count} />;
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
